import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { AdminMetricsTiles } from "@/components/cre/admin/AdminMetricsTiles";
import { AdminFeedbackAndUsers } from "@/components/cre/admin/AdminFeedbackAndUsers";
import { AdminIncidentTable } from "@/components/cre/admin/AdminIncidentTable";
import { VisitorAnalytics } from "@/components/cre/admin/VisitorAnalytics";
import {
  AdminFullViewDialog,
  AdminStatusDialog,
} from "@/components/cre/admin/AdminDialogs";
import { Activity, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Incident management state
  const [statusFilter, setStatusFilter] = useState("all");
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);

  // Full-view dialog state
  const [fullView, setFullView] = useState(null);
  const [fullLoading, setFullLoading] = useState(false);

  // Status change dialog state
  const [statusEdit, setStatusEdit] = useState(null); // {id, current, next, reason}

  const loadIncidents = useCallback(async (filter) => {
    setIncidentsLoading(true);
    try {
      const data = await api.adminIncidents(
        filter && filter !== "all" ? filter : undefined,
        200
      );
      setIncidents(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load incidents");
    } finally {
      setIncidentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === undefined || user === null || user.role !== "admin") return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [m, a, u] = await Promise.all([
          api.adminMetrics(),
          api.adminActivity(50),
          api.adminUsers(),
        ]);
        if (!cancelled) {
          setMetrics(m);
          setActivity(a);
          setUsers(u);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (user?.role === "admin") loadIncidents(statusFilter);
  }, [user, statusFilter, loadIncidents]);

  const refreshAll = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([
        api.adminMetrics(),
        api.adminActivity(50),
      ]);
      setMetrics(m);
      setActivity(a);
      await loadIncidents(statusFilter);
    } catch (_) {
      /* ignore */
    }
  }, [loadIncidents, statusFilter]);

  const onViewFull = async (id) => {
    setFullLoading(true);
    setFullView({ id, data: null });
    try {
      const data = await api.adminIncidentFull(id);
      setFullView({ id, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load full view");
      setFullView(null);
    } finally {
      setFullLoading(false);
    }
  };

  const onSubmitStatus = async () => {
    if (!statusEdit) return;
    try {
      await api.adminUpdateStatus(
        statusEdit.id,
        statusEdit.next,
        statusEdit.reason || ""
      );
      toast.success(`Status updated to ${statusEdit.next.replaceAll("_", " ")}`);
      setStatusEdit(null);
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const pendingCount = useMemo(
    () => incidents.filter((i) => i.status === "awaiting_approval").length,
    [incidents]
  );

  if (user === undefined) {
    return (
      <div className="min-h-screen cre-grid-bg flex flex-col">
        <Header />
        <div className="flex-1 max-w-7xl mx-auto w-full px-6 py-16 text-center text-slate-500 text-sm">
          <Loader2 className="h-5 w-5 mx-auto animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin" }} />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen cre-grid-bg flex flex-col">
      <Header />
      <div
        className="flex-1 max-w-7xl mx-auto w-full px-5 md:px-8 py-8 md:py-10"
        data-testid="admin-dashboard"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-amber-300" strokeWidth={1.75} />
          <div className="cre-overline text-amber-300">Admin</div>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mt-1">
          Usage &amp; feedback metadata
        </h1>
        <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
          Privacy-preserving overview: registered accounts, analysis counts,
          feedback breakdown, and recent activity metadata. Incident content is
          only surfaced when you explicitly open the full view — which is
          logged into the audit trail.
        </p>

        {loading ? (
          <div className="mt-10 text-center text-sm text-zinc-500">
            <Loader2 className="h-5 w-5 mx-auto animate-spin" />
          </div>
        ) : error ? (
          <div className="mt-10 text-rose-300 text-sm">{error}</div>
        ) : metrics ? (
          <>
            <AdminMetricsTiles metrics={metrics} />
            <VisitorAnalytics />
            <AdminFeedbackAndUsers
              metrics={metrics}
              users={users}
              setUsers={setUsers}
            />
            <AdminIncidentTable
              incidents={incidents}
              incidentsLoading={incidentsLoading}
              statusFilter={statusFilter}
              onFilterChange={setStatusFilter}
              pendingCount={pendingCount}
              onViewFull={onViewFull}
              onEditStatus={setStatusEdit}
            />

            {/* Recent activity metadata */}
            <div
              data-testid="admin-activity-list"
              className="mt-10 cre-surface rounded-sm p-5"
            >
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-300" strokeWidth={1.5} />
                <div className="cre-overline text-blue-300">
                  Recent activity (metadata)
                </div>
              </div>
              <div className="mt-3 space-y-1 max-h-[420px] overflow-auto pr-1">
                {activity.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`admin-activity-${a.id}`}
                    className="flex items-center justify-between text-xs border-b border-white/5 pb-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-zinc-100">
                        {a.title_preview || "(untitled)"}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 truncate">
                        {a.domain || "domain=?"} · {a.severity} · {a.status} ·{" "}
                        {a.user.is_anonymous ? "anonymous" : a.user.email} ·{" "}
                        {a.feedback_count} feedback
                      </div>
                    </div>
                    <span className="font-mono text-[11px] text-zinc-500 ml-3">
                      {a.created_at?.slice(0, 19).replace("T", " ")}
                    </span>
                  </div>
                ))}
                {activity.length === 0 ? (
                  <div className="text-xs text-zinc-500">No activity yet.</div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>

      <AdminFullViewDialog
        fullView={fullView}
        fullLoading={fullLoading}
        onClose={() => setFullView(null)}
      />
      <AdminStatusDialog
        statusEdit={statusEdit}
        setStatusEdit={setStatusEdit}
        onSubmit={onSubmitStatus}
      />
      <Footer />
    </div>
  );
}
